<script lang="ts" setup>import { toRef as _toRef } from 'vue';

import { useMotion } from '@vueuse/motion'
import { isDesktop } from '@consumer/utils/breakpoints'
import { useRecipientLanding } from '@consumer/modules/recipientGiftLandingV2/composables'
import EnvelopeBack from './EnvelopeBack.vue'
import EnvelopeFront from './EnvelopeFront.vue'
import Giftly from './../Giftly.vue'
import GreetingCard from './GreetingCard.vue'

withDefaults(defineProps<{
  animationSpeed: number
}>(), {  })

const __$temp_1 = (useRecipientLanding()),
  gift = _toRef(__$temp_1, 'gift'),
  hasGiftlyImage = _toRef(__$temp_1, 'hasGiftlyImage');

const envelopeBack = ref<InstanceType<typeof EnvelopeBack>>()
const envelopeBackMotion = useMotion(envelopeBack, {
  initial: {
    opacity: 1,
    x: 0,
    y: 0,
    'transform-origin': 'bottom',
    transition: { duration: 0 },
  },
  hide: {
    opacity: 0,
    x: 0,
    y: 500,
    transition: { duration: __props.animationSpeed, ease: 'easeInOut' },
  },
})

const envelopeFront = ref<InstanceType<typeof EnvelopeFront>>()
const envelopeFrontMotion = useMotion(envelopeFront, {
  initial: {
    opacity: 1,
    x: 0,
    y: 145,
    'transform-origin': 'bottom',
    transition: { duration: 0 },
  },
  hide: {
    opacity: 0,
    x: 0,
    y: 647,
    transition: { duration: __props.animationSpeed, ease: 'easeInOut' },
  },
})

const greetingCard = ref<InstanceType<typeof GreetingCard>>()
const greetingCardMotion = useMotion(greetingCard, {
  initial: {
    opacity: 1,
    x: -48,
    y: 0,
    scale: 0.7,
    'transform-origin': 'top right',
    transition: { duration: 0, ease: 'easeInOut' },
  },
  bringToFront: {
    opacity: 1,
    x: -10,
    y: 0,
    scale: 1,
    transition: { duration: __props.animationSpeed, ease: 'easeInOut' },
  },
  dropdown: {
    opacity: 0,
    x: -30,
    y: 372,
    scale: 1,
    transition: { duration: __props.animationSpeed, ease: 'easeInOut' },
  },
  hide: {
    opacity: 0,
  },
})

const giftly = ref<InstanceType<typeof Giftly>>()
const giftlyMotion = useMotion(giftly, {
  initial: {
    opacity: 1,
    x: 0,
    y: 0,
    rotate: -22.1,
    rotateY: 0,
    scale: 0.5,
    transition: { duration: 0 },
  },
  moveLeft: {
    x: -5,
    y: 60,
    rotate: -22.1,
    rotateY: 0,
    scale: 0.7,
    opacity: 1,
    transition: { duration: __props.animationSpeed, ease: 'easeInOut' },
  },
  moveCenter: {
    x: 55,
    y: 70,
    rotate: -5,
    rotateY: 0,
    scale: 0.5,
    opacity: 1,
    transition: { duration: __props.animationSpeed, ease: 'easeInOut' },
  },
  reveal: {
    x: 30,
    y: 100,
    rotate: -5,
    rotateY: 0,
    scale: 0.75,
    opacity: 1,
    'transform-origin': 'center',
    transition: { duration: __props.animationSpeed, ease: 'easeInOut' },
  },
  align: {
    rotate: 0,
    rotateY: 0,
    scale: 0.9,
    x: 40,
    y: 0,
    opacity: 1,
    transition: { transform: { ease: 'easeInOut' }, opacity: { delay: __props.animationSpeed } },
  },
  moveUp: {
    x: -40,
    y: isDesktop.value ? 30 : -10,
    rotate: -5,
    rotateY: 0,
    scale: isDesktop.value ? 0.8 : 0.6,
    opacity: 1,
    transition: { ease: 'easeInOut', duration: __props.animationSpeed },
  },
  skewIn: {
    transitionProperty: 'transform, opacity',
    transitionBehavior: 'smooth',
    transitionTimingFunction: 'ease-in-out',
    transitionDuration: `${__props.animationSpeed}ms`,
    transform: 'translate3d(-40px, 140px, 0px) rotate(0deg) scale(0.9) perspective(700px) rotateY(89deg)',
    opacity: 0,
    transition: {
      transform: { ease: 'easeInOut', duration: __props.animationSpeed },
      opacity: { ease: 'easeInOut', delay: 100 },
    },
  },
  hide: {
    opacity: 0,
    transition: { ease: 'easeInOut' },
  },
})

defineExpose({
  initialize: () => {
    return Promise.allSettled([
      envelopeBackMotion.apply('initial'),
      envelopeFrontMotion.apply('initial'),
      greetingCardMotion.apply('initial'),
      giftlyMotion.apply('initial'),
      greetingCard.value?.close(),
    ])
  },
  unpack: () => {
    return Promise.allSettled([
      envelopeBackMotion.apply('hide'),
      envelopeFrontMotion.apply('hide'),
      greetingCardMotion.apply('bringToFront'),
      giftlyMotion.apply('moveLeft'),
      greetingCard.value?.close(),
    ])
  },
  openGreetingCard: () => {
    return Promise.allSettled([
      envelopeBackMotion.apply('hide'),
      envelopeFrontMotion.apply('hide'),
      giftlyMotion.apply('moveCenter'),
      greetingCardMotion.apply('bringToFront'),
    ]).then(() => greetingCard.value?.open())
  },
  showPhoto: (index = 0) => {
    return Promise.allSettled([
      envelopeBackMotion.apply('hide'),
      envelopeFrontMotion.apply('hide'),
      giftlyMotion.apply('moveCenter'),
      greetingCardMotion.apply('bringToFront'),
    ]).then(() => greetingCard.value?.open({ showPhoto: index }))
  },
  showVoiceMemo: () => {
    return Promise.allSettled([
      envelopeBackMotion.apply('hide'),
      envelopeFrontMotion.apply('hide'),
      giftlyMotion.apply('moveCenter'),
      greetingCardMotion.apply('bringToFront'),
    ]).then(() => greetingCard.value?.open({ showVoiceMemo: true }))
  },
  revealGiftly: () => {
    return new Promise<void>((resolve) => {
      Promise.allSettled([
        envelopeBackMotion.apply('hide'),
        envelopeFrontMotion.apply('hide'),
        giftlyMotion.apply('moveCenter'),
        greetingCardMotion.apply('bringToFront'),
        greetingCard.value?.close(),
      ]).then(() =>
        greetingCardMotion.apply('dropdown')?.then(() => {
          giftlyMotion.apply('reveal')?.then(() => {
            resolve()
          })
        }),
      )
    })
  },
  align: () => {
    return Promise.allSettled([
      envelopeBackMotion.apply('hide'),
      envelopeFrontMotion.apply('hide'),
      greetingCardMotion.apply('hide'),
      giftlyMotion.apply('align'),
    ])
  },
})
</script>

<template>
  <div>
    <div class="gift-collage relative" style="height: 372px; width: 305px;">
      <EnvelopeBack ref="envelopeBack" class="absolute"/>
      <Giftly
        v-if="hasGiftlyImage"
        ref="giftly"
        class="absolute top-0"
        :gift="gift"
        :style="{ left: '-70px', width: '388px', height: '259px' }"
      />
      <GreetingCard ref="greetingCard" class="absolute"/>
      <EnvelopeFront ref="envelopeFront" class="absolute"/>
    </div>
  </div>
</template>
